/**
 * @file 計画の本の「今週の範囲」と、本ごとの活動 (いつ・どれだけ触れたか) を出す純関数を置く。
 *
 * @what: 学習スケジュール (model/examSchedule.ts、以下スケジュール) の本ごとの周回の期間から、今日がどの周回のどの週に
 *   当たるかを求め、その週に読む範囲を本の中の割合 [from, to) で返す。割合にするのは、本だなの資料の
 *   ページ数 (連番) と参考書ルートの量 (問・テーマ・ページ) の単位が違うためで、使う側が自分の物差しに
 *   写す。あわせて、読んだ時間と手書きの記録を本 (ISBN) ごとの「日付 → 秒数」にまとめる。
 * @why: 計画と実績を結ぶ手がかりは ISBN しかない (本だなの資料・計画の教材・ルートの本はどれも ISBN を
 *   持てる)。「遅れ」は言わず、今週の位置と触れた跡だけを置く (代表の判断、2026-09-13。「遅れの表示を
 *   出さない」という epic_23の方針に従う)。
 * @failure: 副作用を持たない純関数である。日付の無い本 (量が未登録) は範囲を持たない。
 */

import type { StudyLearningPlan } from "../api/learningPlans";
import type { StudyMaterial, StudyProgressLog } from "../api/plans";
import type { StudyReadingSession } from "../api/readingSessions";
import type { StudySource } from "../api/sources";
import { latestTextbookRoutes, type StudyTextbookRoute } from "../api/textbookRoutes";
import { addDaysLocal } from "../api/dates";
import { buildSchedule, type ScheduledBook, type SubjectSchedule } from "./examSchedule";
import { parseSourceStudyLog } from "./sourceStudyLog";
import { mergeRoutesByTarget } from "./textbookRouteGrid";

/** 計画に入っている本1冊の、今週の範囲である。 */
export interface PlannedBookRange {
  isbn: string;
  title: string;
  subjectLabel: string;
  /** いまの周回 (1始まり)。周回の期間の外なら null になる。 */
  round: number | null;
  /** 今週に読む範囲 (本の中の割合、0〜1)。期間の外・日付の無い本では null になる。 */
  fraction: { from: number; to: number } | null;
  /** 今週の月曜と日曜 (周回の期間で切る)。 */
  weekStart: string | null;
  weekEnd: string | null;
  /** いま (または次) の周回の期間である。 */
  passStart: string | null;
  passEnd: string | null;
  /** 今日が期間より前なら "before"、中なら "in"、後なら "after"、日付が無ければ "none"。 */
  timing: "before" | "in" | "after" | "none";
}

function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  const t0 = Date.UTC(ay ?? 1970, (am ?? 1) - 1, ad ?? 1);
  const t1 = Date.UTC(by ?? 1970, (bm ?? 1) - 1, bd ?? 1);
  return Math.round((t1 - t0) / 86_400_000);
}

/** 1冊の本について、今日の位置から今週の範囲を出す。 */
export function rangeOfBook(
  b: ScheduledBook,
  subjectLabel: string,
  today: string,
): PlannedBookRange | null {
  const isbn = b.book.book.isbn;
  if (!isbn) return null;
  const base = {
    isbn,
    title: b.book.book.title,
    subjectLabel,
    round: null,
    fraction: null,
    weekStart: null,
    weekEnd: null,
    passStart: null,
    passEnd: null,
  };
  if (b.passes.length === 0 || b.startDate === null || b.endDate === null) {
    return { ...base, timing: "none" };
  }
  if (today < b.startDate) {
    const first = b.passes[0]!;
    return { ...base, timing: "before", passStart: first.startDate, passEnd: first.endDate, round: first.round };
  }
  if (today > b.endDate) {
    const last = b.passes[b.passes.length - 1]!;
    return { ...base, timing: "after", passStart: last.startDate, passEnd: last.endDate, round: last.round };
  }
  const pass = b.passes.find((p) => today >= p.startDate && today <= p.endDate) ?? b.passes[b.passes.length - 1]!;
  const days = daysBetween(pass.startDate, pass.endDate) + 1;
  const weeks = Math.max(1, Math.ceil(days / 7));
  const week = Math.min(weeks - 1, Math.floor(daysBetween(pass.startDate, today) / 7));
  const weekStart = addDaysLocal(pass.startDate, week * 7);
  const weekEndRaw = addDaysLocal(weekStart, 6);
  return {
    ...base,
    timing: "in",
    round: pass.round,
    passStart: pass.startDate,
    passEnd: pass.endDate,
    weekStart,
    weekEnd: weekEndRaw < pass.endDate ? weekEndRaw : pass.endDate,
    fraction: { from: week / weeks, to: (week + 1) / weeks },
  };
}

/** 科目ごとのスケジュールから、本 (ISBN) → 今週の範囲 の表を作る。同じ本が2回出たら先の科目のものを使う。 */
export function plannedRangesOf(
  schedule: readonly SubjectSchedule[],
  today: string,
): Map<string, PlannedBookRange> {
  const out = new Map<string, PlannedBookRange>();
  for (const sub of schedule) {
    for (const lane of sub.lanes) {
      for (const b of lane.books) {
        const r = rangeOfBook(b, sub.subjectLabel, today);
        if (r !== null && !out.has(r.isbn)) out.set(r.isbn, r);
      }
    }
  }
  return out;
}

/** 進めている計画すべてのスケジュールを組む (model/shelfSections.ts と同じ選び方)。 */
export function schedulesOfPlans(
  routes: readonly StudyTextbookRoute[],
  plans: readonly StudyLearningPlan[],
  today: string,
): SubjectSchedule[] {
  const out: SubjectSchedule[] = [];
  const latest = latestTextbookRoutes(routes);
  for (const plan of plans) {
    if (plan.status !== "active") continue;
    const mine = latest.filter((r) => r.series_id === plan.route_series_id);
    const merged = mergeRoutesByTarget(mine)[0];
    if (merged === undefined) continue;
    out.push(...buildSchedule(merged, plan, today));
  }
  return out;
}

/** 割合の範囲を、ページ数 (連番) の範囲 [from, to] (1始まり、閉区間) に写す。 */
export function fractionToPages(
  fraction: { from: number; to: number },
  pageCount: number,
): { from: number; to: number } {
  const from = Math.min(pageCount, Math.floor(fraction.from * pageCount) + 1);
  const to = Math.max(from, Math.min(pageCount, Math.ceil(fraction.to * pageCount)));
  return { from, to };
}

/** 本 (ISBN) ごとの、日付 → 実際に操作していた秒数 (手書きの記録は分 × 60、分が無ければ0で日付だけ残す)。 */
export type ActivityByIsbn = ReadonlyMap<string, ReadonlyMap<string, number>>;

/**
 * 読んだ時間と手書きの記録を、本 (ISBN) ごとの日付の表にまとめる。
 *
 * @what: 読んだ時間は資料 id → 資料の ISBN で結ぶ。手書きの記録は、計画の教材 (material_id → 教材の ISBN)
 *   か、資料への記録 (detail の source_id → 資料の ISBN) で結ぶ。ISBN の無い資料・教材の分は落とす。
 */
export function activityByIsbn(
  sessions: readonly StudyReadingSession[],
  logs: readonly StudyProgressLog[],
  sources: readonly StudySource[],
  materials: readonly StudyMaterial[],
): Map<string, Map<string, number>> {
  const isbnOfSource = new Map<string, string>();
  for (const s of sources) if (s.isbn) isbnOfSource.set(s.id, s.isbn);
  const isbnOfMaterial = new Map<string, string>();
  for (const m of materials) if (m.isbn) isbnOfMaterial.set(m.id, m.isbn);

  const out = new Map<string, Map<string, number>>();
  const add = (isbn: string, date: string, seconds: number) => {
    let days = out.get(isbn);
    if (!days) {
      days = new Map();
      out.set(isbn, days);
    }
    days.set(date, (days.get(date) ?? 0) + seconds);
  };
  for (const s of sessions) {
    const isbn = isbnOfSource.get(s.source_id);
    if (isbn) add(isbn, s.date, s.active_seconds);
  }
  for (const log of logs) {
    const detail = parseSourceStudyLog(log);
    const isbn =
      (detail !== null ? isbnOfSource.get(detail.source_id) : undefined) ??
      (log.material_id ? isbnOfMaterial.get(log.material_id) : undefined);
    if (isbn) add(isbn, log.date, (log.minutes ?? 0) * 60);
  }
  return out;
}

/** 本の集まりについて、期間 [from, to] の秒数の合計を返す。 */
export function activitySeconds(
  activity: ActivityByIsbn,
  isbns: Iterable<string | undefined>,
  from: string,
  to: string,
): number {
  let total = 0;
  for (const isbn of isbns) {
    if (!isbn) continue;
    const days = activity.get(isbn);
    if (!days) continue;
    for (const [date, sec] of days) if (date >= from && date <= to) total += sec;
  }
  return total;
}

/** 本の集まりについて、期間 [from, to] に触れた日があるかを返す。 */
export function touchedWithin(
  activity: ActivityByIsbn,
  isbns: Iterable<string | undefined>,
  from: string,
  to: string,
): boolean {
  for (const isbn of isbns) {
    if (!isbn) continue;
    const days = activity.get(isbn);
    if (!days) continue;
    for (const date of days.keys()) if (date >= from && date <= to) return true;
  }
  return false;
}
